import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';

export default function NotesPanel({ projectId, taskId, user }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState('');
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);

  const table = taskId ? 'task_notes' : 'notes';
  const idField = taskId ? 'task_id' : 'project_id';
  const idValue = taskId || projectId;

  const load = async () => {
    const { data } = await supabase
      .from(table)
      .select('*')
      .eq(idField, idValue)
      .order('created_at', { ascending: false });
    setNotes(data || []);
  };

  useEffect(() => { if (idValue) load(); }, [idValue]);

  const addNote = async () => {
    if (!text.trim()) return;
    setSaving(true);
    const userName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Unknown';
    await supabase.from(table).insert({
      [idField]: idValue,
      content: text.trim(),
      created_by: user?.id || null,
      created_by_name: userName
    });
    setText('');
    await load();
    setSaving(false);
  };

  const saveEdit = async (id) => {
    if (!editText.trim()) return;
    await supabase.from(table).update({
      content: editText.trim(),
      updated_at: new Date().toISOString()
    }).eq('id', id);
    setEditing(null);
    await load();
  };

  const deleteNote = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    await supabase.from(table).delete().eq('id', id);
    await load();
  };

  const fmt = (ts) => {
    const d = new Date(ts);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
      ' · ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const canEdit = (note) => note.created_by === user?.id;

  return (
    <div>
      <label style={{ fontSize: 12, color: 'var(--accent)', display: 'block', marginBottom: 12 }}>NOTES</label>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <textarea
          rows={2}
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Add a note..."
          style={{ flex: 1, resize: 'vertical' }}
          onKeyDown={e => { if (e.key === 'Enter' && e.metaKey) addNote(); }}
        />
        <button
          className="btn-primary"
          onClick={addNote}
          disabled={saving || !text.trim()}
          style={{ alignSelf: 'flex-end', whiteSpace: 'nowrap' }}
        >
          {saving ? '...' : 'Add'}
        </button>
      </div>

      {notes.length === 0 ? (
        <p style={{ color: 'var(--text3)', fontSize: 13 }}>No notes yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {notes.map(n => (
            <div key={n.id} style={{
              background: 'var(--bg3)', borderRadius: 'var(--radius)',
              padding: '12px 14px',
              borderLeft: canEdit(n) ? '3px solid var(--accent)' : '3px solid var(--border2)'
            }}>
              {editing === n.id ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  <textarea
                    rows={3}
                    value={editText}
                    onChange={e => setEditText(e.target.value)}
                    style={{ flex: 1 }}
                    autoFocus
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <button className="btn-primary" style={{ padding: '5px 10px', fontSize: 12 }} onClick={() => saveEdit(n.id)}>Save</button>
                    <button className="btn-ghost" style={{ padding: '5px 10px', fontSize: 12 }} onClick={() => setEditing(null)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <p style={{ fontSize: 13, color: 'var(--text)', marginBottom: 8, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {n.content}
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                      <strong style={{ color: 'var(--text2)' }}>{n.created_by_name}</strong>
                      {' · '}
                      {fmt(n.created_at)}
                      {n.updated_at && n.updated_at !== n.created_at && (
                        <span style={{ marginLeft: 6, fontStyle: 'italic' }}>(edited)</span>
                      )}
                    </div>
                    {canEdit(n) && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn-ghost" style={{ padding: '3px 8px', fontSize: 11 }}
                          onClick={() => { setEditing(n.id); setEditText(n.content); }}>Edit</button>
                        <button className="btn-danger" style={{ padding: '3px 8px', fontSize: 11 }}
                          onClick={() => deleteNote(n.id)}>Delete</button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
